/**
 * Calcula sugerencias proactivas de trade para mostrar en el home.
 *
 * Objetivo: el usuario abre la app, ve "Edwin tiene 5 láminas que te
 * faltan", clickea y va directo a armar el trato. Reemplaza el flujo
 * actual de "tienes que ir a Mercado Global → filtrar → encontrar →
 * abrir cada perfil".
 *
 * Inputs:
 *   - friendsList: array de { uid, name, stats: { match: { receive,
 *     give, score } } } como lo construye AlbumApp.sortedFriendsList.
 *   - trades: state.trades — para skipear amigos con trato activo.
 *   - dismissedMap: { friendUid: timestamp } del localStorage snooze.
 *   - now: Date.now() (parametrizable para tests).
 *   - max: cuántas sugerencias devolver (default 3).
 *
 * Reglas para que un amigo aparezca:
 *   - Es coleccionista real (uid no empieza con 'ext-').
 *   - match.score > 0 (hay láminas posibles a intercambiar).
 *   - No tiene trato reservado o activo con el usuario.
 *   - No fue dismissed en las últimas 24h.
 *
 * Orden: por match.score descendente. Truncado a `max`.
 */

const SNOOZE_MS = 24 * 60 * 60 * 1000;
const SNOOZE_KEY_PREFIX = 'tradeSuggestionsSnooze_';

export function computeTradeSuggestions({
  friendsList = [],
  trades = {},
  dismissedMap = {},
  now = Date.now(),
  max = 3,
} = {}) {
  return friendsList
    .filter(f => {
      // 1. No ext-*: las sugerencias son para tratos asincrónicos
      //    con coleccionistas que sí están en la app.
      if (!f.uid || f.uid.startsWith('ext-')) return false;
      // 2. Match útil: si no hay láminas que intercambiar, no tiene
      //    sentido sugerir.
      const score = f.stats?.match?.score || 0;
      if (score <= 0) return false;
      // 3. No proponer cuando ya hay trato en curso. Evita ruido
      //    redundante — ya tienen un canal abierto.
      const trade = trades[f.uid];
      if (trade && (trade.reserved || (trade.give?.length || 0) + (trade.receive?.length || 0) > 0)) {
        return false;
      }
      // 4. Respetar el snooze del usuario.
      const snoozedUntil = dismissedMap[f.uid] || 0;
      if (snoozedUntil > now) return false;
      return true;
    })
    .sort((a, b) => (b.stats?.match?.score || 0) - (a.stats?.match?.score || 0))
    .slice(0, max);
}

/** Carga el snooze map del localStorage. */
export function loadSuggestionsSnooze(uid) {
  if (!uid) return {};
  try {
    const raw = localStorage.getItem(SNOOZE_KEY_PREFIX + uid);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/** Snooza una sugerencia por 24h. */
export function snoozeSuggestion(uid, friendUid) {
  if (!uid || !friendUid) return;
  try {
    const current = loadSuggestionsSnooze(uid);
    current[friendUid] = Date.now() + SNOOZE_MS;
    localStorage.setItem(SNOOZE_KEY_PREFIX + uid, JSON.stringify(current));
  } catch {
    // localStorage lleno — silencioso.
  }
}

export const __internals = { SNOOZE_MS, SNOOZE_KEY_PREFIX };
